import { faroCoreApiClient } from "@api/faro-core-api-client";
import { SelectManagerDialog } from "@components/table/members/remove-xg-seat/select-manager-dialog";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";
import { useToast } from "@flat-ui";
import { updateMemberRoleInGroup } from "@store/groups/groups-slice-thunk";
import { useAppDispatch } from "@store/store-helper";
import { getPrettyName } from "@utils/user-utils";
/**
 * Dialog that asks for a new group manager of a group whose only group manager is the member, since
 * the backend does not let the last group manager leave a group or step down. The new group manager
 * is picked from the members of the workspace holding a Sphere XG seat, and is added to the group
 * when they do not belong to it yet.
 */
export function AssignGroupManagerDialog({
  member,
  group,
  groupMembers,
  onClose,
  onAssigned
}) {
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const {
    handleErrorWithToast
  } = useErrorContext();
  const {
    openToast
  } = useToast();
  return <SelectManagerDialog member={member} title="Assign a new group manager" description={<>
          <strong>
            <var>{getPrettyName(member)}</var>
          </strong>{" "}
          is the only group manager of{" "}
          <strong>
            <var>{group.name}</var>
          </strong>
          . Choose the new group manager, then their role in the group can be
          changed.
        </>} dataTestId="assign-group-manager-dialog" onClose={onClose} onAssign={assignManager} />;

  /**
   * Makes the member the group manager, and tells the user once that worked
   *
   * @returns whether that worked
   */
  async function assignManager(newManager) {
    const isInGroup = groupMembers.some(({
      identity
    }) => identity === newManager.identity);
    const isAssigned = isInGroup ? await promoteMember(newManager) : await addManager(newManager);
    if (!isAssigned) {
      return false;
    }
    openToast({
      title: "Group manager assigned",
      message: <>
          <strong>
            <var>{getPrettyName(newManager)}</var>
          </strong>{" "}
          now manages <var>{group.name}</var>.
        </>
    });
    onAssigned();
    return true;
  }

  /**
   * Makes a member of the group its group manager.
   *
   * @returns whether that worked. The error toast is shown by the errors slice.
   */
  async function promoteMember(newManager_0) {
    try {
      await dispatch(updateMemberRoleInGroup({
        companyId,
        groupId: group.id,
        userIdentity: newManager_0.identity,
        role: "COMPANY_MANAGER"
      })).unwrap();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Adds a member of the workspace who does not belong to the group yet as its group manager.
   *
   * @returns whether that worked, telling the user why not otherwise
   */
  async function addManager(newManager_1) {
    try {
      const response = await faroCoreApiClient.groups.addMembersToGroup({
        companyId,
        groupId: group.id,
        payload: {
          assignments: [{
            identities: [newManager_1.identity],
            role: "COMPANY_MANAGER"
          }]
        }
      });
      const failure = response.errorData?.details.failedInvitations[0];
      if (failure) {
        throw new Error(failure.message);
      }
      return true;
    } catch (error) {
      handleErrorWithToast({
        id: `assignGroupManager-${Date.now().toString()}`,
        title: "Cannot assign the group manager",
        error
      });
      return false;
    }
  }
}